// Redesign phase 3 (docs/ui_redesign/03-invoice-editor-common.md): the pieces of the new editor layout that don't need React.

// The required-field messages come from InvoiceDocumentEditor's getErrorMessage(), in the app's own words.
// Each maps to the label of the field it is about, so the banner can jump there. 'lines' means the line grid.
const JUMPS = [
	[/^Vendor\b/i, 'ERP Vendor/Site'],
	[/Invoice Number/i, 'Invoice Number'],
	[/Invoice Date/i, 'Invoice Date'],
	[/Due Date/i, 'Due Date'],
	[/GL Date/i, 'GL Date'],
	[/Payment Term/i, 'Payment Term'],
	[/Service Term Start/i, 'Service Term From'],
	[/Service Term End/i, 'Service Term To'],
	[/^Start Date/i, 'Start Date'],
	[/^End Date/i, 'End Date'],
	[/Currency/i, 'Currency'],
	[/Total Spot/i, 'Total Spots'],
	[/Invoice Total/i, 'Invoice Total'],
	[/Media Type/i, 'Media Type'],
	[/Call Letters/i, 'Call Letters'],
	[/^Band\b/i, 'Band'],
	[/select a Client/i, 'Client'],
	[/select a Product/i, 'Product'],
	[/PO Code/i, 'PO Code'],
	[/^Taxes/i, 'Tax Code'],
	[/line #|detail line|Detail Lines|spot lines/i, 'lines']
];

export const REQUIRED_HEADER = 'The invoice encountered the following errors';

// Quick fixes: open the editor's own lookup for the missing value (the same control the user would click).
const FIXES = [
	[/^Vendor is required/i, 'Find vendor', { selector: 'img[alt="Search Vendor"]' }],
	[/select a Client/i, 'Find client', { selector: 'img[alt="Search Client"]' }],
	[/select a Product/i, 'Find product', { selector: 'img[alt="Search Product"]' }],
	[/PO Code is required/i, 'Find PO', { selector: 'img[alt="Search PO Code"]' }],
	[/IO Number is invalid at line #(\d+)/i, 'Find IO/PO', { line: true }]
];

// details: the invoice's lines, to turn "line #N" into that line's IO search button.
export function fixFor(message, details = []) {
	const hit = FIXES.find(([re]) => re.test(message || ''));
	if (!hit) return null;
	const [re, label, how] = hit;
	if (how.line) {
		const n = Number((message.match(re) || [])[1]);
		const line = details[n - 1];
		return line && line.seqId != null ? { label, selector: `#search-io-po-edit-${line.seqId}` } : null;
	}
	return { label, selector: how.selector };
}

// Runs a fix by clicking the control, if it is on screen (it is hidden when the field is read-only).
export function runFix(root, fix) {
	const el = root && fix && root.querySelector(fix.selector);
	if (!el || el.offsetParent === null) return false;
	el.scrollIntoView({ block: 'center', behavior: 'smooth' });
	el.click();
	return true;
}

export function jumpTargetFor(message) {
	const hit = JUMPS.find(([re]) => re.test(message || ''));
	return hit ? hit[1] : null;
}

// The banner under the editor header: what stops a save, then the other problems on the invoice.
export function editorProblems({
	requiredList = [], readOnly = false, invoiceTotal = 0, scannedTotal = 0, discrepancyAccepted = false,
	deliveryMismatch = false, deliveryMessage = '', statusId = 0, postingError = '', formatAmount = String, details = [], approvalGuide = false
}) {
	const items = [];
	if (!readOnly) {
		requiredList
			.filter(m => m && m !== REQUIRED_HEADER)
			.forEach((m, i) => {
				const text = m.replace(/^-\s*/, '');
				// The list Save shows and stops at: these block the save.
				items.push({ key: 'req' + i, tone: 'bad', blocks: true, text, jump: jumpTargetFor(text), fix: fixFor(text, details) });
			});
	}
	const scanned = parseFloat(scannedTotal) || 0;
	if (scanned !== 0 && !discrepancyAccepted && Math.abs((parseFloat(invoiceTotal) || 0) - scanned) > 0.001) {
		items.push({ key: 'scanned', tone: 'warn', text: `Amount does not match scanned total (${formatAmount(scanned)})`, jump: 'Invoice Total' });
	}
	if (deliveryMismatch) {
		items.push({ key: 'delivery', tone: 'warn', text: (deliveryMessage || '').trim() || 'Delivery Amount Discrepant!', jump: 'lines' });
	}
	if (Number(statusId) === 180) {
		items.push({ key: 'post', tone: 'bad', text: postingError ? `Post failed: ${postingError}` : 'Post failed', jump: null });
	}
	// Approving: the line tools are below the fold and need a ticked line first, so say what to do (R9).
	if (approvalGuide) items.push({ key: 'approve', tone: 'info', text: 'Select lines, then Approve or Reject. Save applies it.', jump: 'lines' });
	return items;
}

// Where the invoice is in its life, for the header's stepper: Scanned → Correct → Approve → Post → Posted.
// levels is the module's number of approval levels (50 is waiting for L1; 50 + 20·levels is fully approved).
export const STAGES = ['Scanned', 'Correct', 'Approve', 'Post', 'Posted'];
export function lifecycleStage(statusId, levels = 1) {
	const s = Number(statusId) || 0;
	const n = Math.max(1, Number(levels) || 1);
	if (s === 170) return { current: 4, done: true, failed: false };
	if (s === 180) return { current: 3, done: false, failed: true };
	const rejected = s >= 80 && s <= 160 && s % 20 === 0;
	if (s < 50 || s === 60 || rejected) return { current: 1, done: false, failed: false, returned: rejected || s === 60 };
	if (s >= 50 + 20 * n) return { current: 3, done: false, failed: false };
	return { current: 2, done: false, failed: false, level: Math.floor((s - 50) / 20) + 1, levels: n };
}

// What stops Save (the required-field list) and what only needs a look (the scan mismatch, a failed post, delivery).
export const problemCounts = problems => ({
	blocking: problems.filter(p => p.blocks).length,
	review: problems.filter(p => !p.blocks && p.tone !== 'info').length
});

// Approval mode shows its next step when the lines can be approved or rejected here (the line tools' own conditions).
export function canApproveLines({ statusId, modelDefId, mode, details = [] }) {
	const s = Number(statusId) || 0;
	return Number(modelDefId) !== 3 && String(mode || '').toLowerCase() !== 'test' && s >= 50 && s !== 60 && s !== 170 && s !== 180
		&& details.some(l => l && l.hasAuthority === 'Y' && Number(l.lineStatus) >= 50);
}

// One primary action, picked from the buttons the editor would show. candidates: [[key, visible], ...] in priority order.
export function pickPrimary(candidates) {
	const hit = candidates.find(([, visible]) => visible);
	return hit ? hit[0] : null;
}

// Scroll to a field by its label (or to the line grid) and focus it.
export function jumpToField(root, target) {
	if (!root || !target) return false;
	let el = null;
	if (target === 'lines') {
		el = root.querySelector('#apw2-lines') || root.querySelector('.MuiDataGrid-root'); // the Lines card (its grid is a table)
	} else {
		const want = target.toLowerCase();
		const label = [...root.querySelectorAll('.row-form label')]
			.find(l => l.offsetParent !== null && l.textContent.replace(/[:\s]+$/, '').trim().toLowerCase() === want);
		el = label ? label.closest('.row-form') : null;
	}
	if (!el) return false;
	el.scrollIntoView({ block: 'center', behavior: 'smooth' });
	const input = el.querySelector('input:not([readonly]):not([type="hidden"]), select, textarea');
	if (input) input.focus({ preventScroll: true });
	el.classList.add('apw2-flash');
	setTimeout(() => el.classList.remove('apw2-flash'), 1600);
	return true;
}
